import { findAllDeliveries } from '../../../api/deliveries.api';
import { queryKeys } from '../../../api/queryKeys';
import type { SimpleVDriveDeliveryEntity } from '../../../types/deliveries.types';
import type { vDriveDeliveryStatus } from '../../../types/api/wallet.types';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';

const PAGE_SIZE = 30;

/** Root margin (px) below viewport - fetch next page when sentinel is this far from bottom */
const PRELOAD_ROOT_MARGIN = '1000px';

const parseIds = (param: string | null): number[] =>
  param
    ? param
        .split(',')
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !Number.isNaN(id))
    : [];

export type DeliveriesFilters = {
  from: string;
  to: string;
  status: vDriveDeliveryStatus | 'allStatuses' | null;
  query: string | null;
  locationIdsParam: string | null;
  brandIdsParam: string | null;
};

function buildDeliveriesParams(filters: DeliveriesFilters, page: number) {
  const { from, to, status, query, locationIdsParam, brandIdsParam } = filters;

  const locationIds = parseIds(locationIdsParam);
  const brandIds = parseIds(brandIdsParam);

  return {
    page,
    limit: PAGE_SIZE,
    from,
    to,
    query: query || null,
    status: status && status !== 'allStatuses' ? status : null,
    locationIds: locationIds.length ? locationIds : null,
    brandIds: brandIds.length ? brandIds : null,
  };
}

async function fetchDeliveriesPage(
  filters: DeliveriesFilters,
  pageParam: number
) {
  const params = buildDeliveriesParams(filters, pageParam);
  const { data } = await findAllDeliveries(params);
  const items = data?.data ?? [];
  const nextPage = items.length === PAGE_SIZE ? pageParam + 1 : undefined;

  return { deliveries: items, nextPage };
}

const SCROLL_KEY = 'admin-deliveries-scroll';

export function useDeliveriesInfiniteScroll(filters: DeliveriesFilters) {
  const observerRef = useRef<HTMLDivElement | null>(null);

  const deliveriesQuery = useInfiniteQuery({
    queryKey: [
      ...queryKeys.deliveries.all,
      filters.from,
      filters.to,
      filters.status,
      filters.query,
      filters.locationIdsParam,
      filters.brandIdsParam,
    ],
    queryFn: ({ pageParam }) => fetchDeliveriesPage(filters, pageParam),
    getNextPageParam: (last) => last.nextPage,
    initialPageParam: 1,
    staleTime: 5 * 60 * 1000, // 5 minutes - keeps cache for scroll restoration
  });

  useEffect(() => {
    const el = observerRef.current;
    if (!el) return;

    const { hasNextPage, fetchNextPage, isFetchingNextPage, isFetching } =
      deliveriesQuery;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage &&
          !isFetching
        ) {
          fetchNextPage();
        }
      },
      {
        threshold: 0,
        rootMargin: `0px 0px ${PRELOAD_ROOT_MARGIN} 0px`,
      }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [
    deliveriesQuery.hasNextPage,
    deliveriesQuery.fetchNextPage,
    deliveriesQuery.isFetchingNextPage,
    deliveriesQuery.isFetching,
  ]);

  const deliveries = useMemo(
    () =>
      deliveriesQuery.data?.pages.flatMap((p) => p.deliveries) ??
      ([] as SimpleVDriveDeliveryEntity[]),
    [deliveriesQuery.data]
  );

  const saveScrollPosition = useCallback(() => {
    sessionStorage.setItem(SCROLL_KEY, String(window.scrollY));
  }, []);

  return {
    deliveries,
    observerRef,
    isFetchingNextPage: deliveriesQuery.isFetchingNextPage,
    isFetching: deliveriesQuery.isFetching,
    status: deliveriesQuery.status,
    hasNextPage: deliveriesQuery.hasNextPage,
    refetch: deliveriesQuery.refetch,
    saveScrollPosition,
  };
}
